/**
 * Drivers Commentary Form Component
 * Matches the Qt C++ DriversModel fields
 */

import React, { useState, useEffect } from 'react';
import { DriversCommentary, DRIVER_REPORT_OPTIONS } from '@rti/shared/src/types';
import { driversAPI } from '@/services/commentary.service';
import RichTextEditor from '@/components/RichTextEditor';
import { useAuth } from '@/contexts/AuthContext';

interface DriversFormProps {
  onSave?: (data: DriversCommentary) => void;
  onCancel?: () => void;
  initialData?: Partial<DriversCommentary>;
}

export const DriversForm: React.FC<DriversFormProps> = ({
  onSave,
  onCancel,
  initialData,
}) => {
  const { user } = useAuth();

  const generateUniqueId = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 9);
    return `DRV-${timestamp}-${random}`;
  };

  const getNextFriday = (date: Date = new Date()): string => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const localDate = new Date(year, month, day);
    const currentDay = localDate.getDay();
    const daysUntilFriday = currentDay <= 5 ? 5 - currentDay : 7 - currentDay + 5;
    localDate.setDate(day + daysUntilFriday);

    const y = localDate.getFullYear();
    const m = String(localDate.getMonth() + 1).padStart(2, '0');
    const d = String(localDate.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getAllFridays = (): string[] => {
    const fridays: string[] = [];
    const today = new Date();
    const oneWeekAgo = new Date(today);
    oneWeekAgo.setDate(today.getDate() - 7);
    const sevenWeeksAhead = new Date(today);
    sevenWeeksAhead.setDate(today.getDate() + (7 * 7));

    let currentDate = new Date(oneWeekAgo);

    while (currentDate <= sevenWeeksAhead) {
      if (currentDate.getDay() === 5) {
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        fridays.push(`${year}-${month}-${day}`);
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return fridays;
  };

  const validFridays = getAllFridays();

  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const [formData, setFormData] = useState<Partial<DriversCommentary>>({
    driverRptUnqId: initialData?.driverRptUnqId || generateUniqueId(),
    date: initialData?.date || getNextFriday(),
    driverReport: initialData?.driverReport || '',
    title: initialData?.title || '',
    marketOverviewBody: initialData?.marketOverviewBody || '',
    marketOverviewBodyTrial: initialData?.marketOverviewBodyTrial || '',
    supplyDemandTitle: initialData?.supplyDemandTitle || '',
    supplyDemandBody: initialData?.supplyDemandBody || '',
    feedstocksBody: initialData?.feedstocksBody || '',
    internationalBody: initialData?.internationalBody || '',
    actionStrategies: initialData?.actionStrategies || '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [currentRecordPendingChange, setCurrentRecordPendingChange] = useState<DriversCommentary | null>(null);
  const [originalData, setOriginalData] = useState<Partial<DriversCommentary>>({});
  const [isNewPendingRecord, setIsNewPendingRecord] = useState(false);

  useEffect(() => {
    if (initialData) {
      const checkForPendingChange = async () => {
        try {
          const pending = await driversAPI.getPending();
          const published = await driversAPI.getAll();

          const matchingPending = pending.find(p => {
            return p.date === initialData.date &&
                   p.driverReport === initialData.driverReport;
          });

          const existsInPublished = published.some(pub => {
            return pub.date === initialData.date &&
                   pub.driverReport === initialData.driverReport;
          });

          if (matchingPending) {
            setCurrentRecordPendingChange(matchingPending);
            setIsNewPendingRecord(!existsInPublished);

            if (existsInPublished) {
              const publishedRecord = published.find(pub =>
                pub.date === initialData.date &&
                pub.driverReport === initialData.driverReport
              );
              setOriginalData(publishedRecord || {});
            } else {
              setOriginalData({});
            }

            setFormData({
              driverRptUnqId: matchingPending.driverRptUnqId || generateUniqueId(),
              date: matchingPending.date || getNextFriday(),
              driverReport: matchingPending.driverReport || '',
              title: matchingPending.title || '',
              marketOverviewBody: matchingPending.marketOverviewBody || '',
              marketOverviewBodyTrial: matchingPending.marketOverviewBodyTrial || '',
              supplyDemandTitle: matchingPending.supplyDemandTitle || '',
              supplyDemandBody: matchingPending.supplyDemandBody || '',
              feedstocksBody: matchingPending.feedstocksBody || '',
              internationalBody: matchingPending.internationalBody || '',
              actionStrategies: matchingPending.actionStrategies || '',
            });
          } else {
            setCurrentRecordPendingChange(null);
            setOriginalData({});
            setIsNewPendingRecord(false);
            setFormData({
              driverRptUnqId: initialData.driverRptUnqId || generateUniqueId(),
              date: initialData.date || getNextFriday(),
              driverReport: initialData.driverReport || '',
              title: initialData.title || '',
              marketOverviewBody: initialData.marketOverviewBody || '',
              marketOverviewBodyTrial: initialData.marketOverviewBodyTrial || '',
              supplyDemandTitle: initialData.supplyDemandTitle || '',
              supplyDemandBody: initialData.supplyDemandBody || '',
              feedstocksBody: initialData.feedstocksBody || '',
              internationalBody: initialData.internationalBody || '',
              actionStrategies: initialData.actionStrategies || '',
            });
          }
        } catch (error) {
          console.error('Error checking pending changes:', error);
        }
      };

      checkForPendingChange();
    }
  }, [initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const hasFieldChanged = (fieldName: keyof DriversCommentary): boolean => {
    if (!currentRecordPendingChange || isNewPendingRecord || !originalData) {
      return false;
    }
    const currentValue = formData[fieldName];
    const originalValue = originalData[fieldName];
    return currentValue !== originalValue;
  };

  const cleanHtml = (html: string): string => {
    if (!html) return '';
    const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    return text ? html : '';
  };

  const validateForm = (): boolean => {
    if (!formData.driverRptUnqId || !formData.date || !formData.driverReport) {
      setError('Driver Report Unique ID, Date, and Driver Report are required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validateForm()) return;

    setLoading(true);
    try {
      const userName = user ? `${user.first_name} ${user.last_name}` : 'Unknown User';
      const dataToSave: DriversCommentary = {
        ...formData,
        marketOverviewBody: cleanHtml(formData.marketOverviewBody || ''),
        marketOverviewBodyTrial: cleanHtml(formData.marketOverviewBodyTrial || ''),
        supplyDemandBody: cleanHtml(formData.supplyDemandBody || ''),
        feedstocksBody: cleanHtml(formData.feedstocksBody || ''),
        internationalBody: cleanHtml(formData.internationalBody || ''),
        actionStrategies: cleanHtml(formData.actionStrategies || ''),
        lastUpdatedBy: userName,
        lastUpdatedTime: new Date().toISOString(),
      } as DriversCommentary;

      await driversAPI.create(dataToSave);
      setSuccess(true);
      if (onSave) onSave(dataToSave);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save data');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      driverRptUnqId: generateUniqueId(),
      date: getNextFriday(),
      driverReport: '',
      title: '',
      marketOverviewBody: '',
      marketOverviewBodyTrial: '',
      supplyDemandTitle: '',
      supplyDemandBody: '',
      feedstocksBody: '',
      internationalBody: '',
      actionStrategies: '',
    });
    setError(null);
    setSuccess(false);
  };

  return (
    <div className="drivers-form bg-rs-black text-rs-off-white p-6 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl font-bold text-rs-green">
          Drivers Commentary (Weekly)
        </h2>
        {currentRecordPendingChange && isNewPendingRecord && (
          <span className="px-3 py-1 bg-rs-salmon/20 text-rs-salmon text-sm rounded flex items-center gap-2">
            <span className="w-2 h-2 bg-rs-salmon rounded-full animate-pulse"></span>
            New Pending Record
          </span>
        )}
      </div>
      <p className="text-sm text-rs-off-white/60 mb-6">
        Record ID: <span className="text-rs-green font-mono">{formData.driverRptUnqId}</span>
      </p>

      {error && (
        <div className="bg-red-900/20 border border-red-500 text-red-400 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-900/20 border border-green-500 text-green-400 px-4 py-3 rounded mb-4">
          Data saved successfully!
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Date {!initialData && '(Friday only)'} *
            </label>
            {initialData ? (
              <input
                type="date"
                name="date"
                value={formData.date || ''}
                onChange={handleChange}
                className="w-full bg-rs-jet border border-rs-jet-light rounded px-4 py-2 text-rs-off-white focus:border-rs-green focus:outline-none opacity-60 cursor-not-allowed"
                required
                readOnly
                disabled
              />
            ) : (
              <select
                name="date"
                value={formData.date || ''}
                onChange={handleChange}
                className="w-full bg-rs-jet border border-rs-jet-light rounded px-4 py-2 text-rs-off-white focus:border-rs-green focus:outline-none"
                required
              >
                <option value="">Select a Friday date...</option>
                {validFridays.map((friday) => (
                  <option key={friday} value={friday}>
                    {formatDate(friday)}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Driver Report *
            </label>
            <select
              name="driverReport"
              value={formData.driverReport || ''}
              onChange={handleChange}
              className="w-full bg-rs-jet border border-rs-jet-light rounded px-4 py-2 text-rs-off-white focus:border-rs-green focus:outline-none"
              required
            >
              <option value="">Select a driver report...</option>
              {DRIVER_REPORT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Title</label>
          <input
            type="text"
            name="title"
            value={formData.title || ''}
            onChange={handleChange}
            className="w-full bg-rs-jet border border-rs-jet-light rounded px-4 py-2 text-rs-off-white focus:border-rs-green focus:outline-none"
          />
        </div>

        {/* Market Overview Body */}
        {currentRecordPendingChange && !isNewPendingRecord && originalData.marketOverviewBody && (
          <div className="bg-rs-jet border border-rs-jet-light rounded px-4 py-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-rs-off-white">Current Production - Market Overview:</span>
              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">Read-Only</span>
            </div>
            <div
              className="text-sm text-rs-off-white/80 p-3 bg-rs-black rounded border border-rs-jet-light"
              dangerouslySetInnerHTML={{ __html: originalData.marketOverviewBody }}
            />
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium">
              Market Overview Body
            </label>
          </div>
          <div className={hasFieldChanged('marketOverviewBody') ? 'ring-2 ring-rs-salmon/50 rounded' : ''}>
            <RichTextEditor
              value={formData.marketOverviewBody || ''}
              onChange={(value) => setFormData((prev) => ({ ...prev, marketOverviewBody: value }))}
              placeholder="Enter market overview commentary..."
              rows={6}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Market Overview Body — Trial
          </label>
          <p className="text-xs text-rs-off-white/40 mb-2">Redacted version shown to free-trial users.</p>
          <div className={hasFieldChanged('marketOverviewBodyTrial') ? 'ring-2 ring-rs-salmon/50 rounded' : ''}>
            <RichTextEditor
              value={formData.marketOverviewBodyTrial || ''}
              onChange={(value) => setFormData((prev) => ({ ...prev, marketOverviewBodyTrial: value }))}
              placeholder="Enter trial-visible market overview commentary..."
              rows={4}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Supply Demand Title
          </label>
          <input
            type="text"
            name="supplyDemandTitle"
            value={formData.supplyDemandTitle || ''}
            onChange={handleChange}
            className="w-full bg-rs-jet border border-rs-jet-light rounded px-4 py-2 text-rs-off-white focus:border-rs-green focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Supply Demand Body
          </label>
          <div className={hasFieldChanged('supplyDemandBody') ? 'ring-2 ring-rs-salmon/50 rounded' : ''}>
            <RichTextEditor
              value={formData.supplyDemandBody || ''}
              onChange={(value) => setFormData((prev) => ({ ...prev, supplyDemandBody: value }))}
              placeholder="Enter supply demand commentary..."
              rows={6}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Feedstocks Body
          </label>
          <div className={hasFieldChanged('feedstocksBody') ? 'ring-2 ring-rs-salmon/50 rounded' : ''}>
            <RichTextEditor
              value={formData.feedstocksBody || ''}
              onChange={(value) => setFormData((prev) => ({ ...prev, feedstocksBody: value }))}
              placeholder="Enter feedstocks commentary..."
              rows={6}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            International Body
          </label>
          <div className={hasFieldChanged('internationalBody') ? 'ring-2 ring-rs-salmon/50 rounded' : ''}>
            <RichTextEditor
              value={formData.internationalBody || ''}
              onChange={(value) => setFormData((prev) => ({ ...prev, internationalBody: value }))}
              placeholder="Enter international commentary..."
              rows={6}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Action Strategies
          </label>
          <div className={hasFieldChanged('actionStrategies') ? 'ring-2 ring-rs-salmon/50 rounded' : ''}>
            <RichTextEditor
              value={formData.actionStrategies || ''}
              onChange={(value) => setFormData((prev) => ({ ...prev, actionStrategies: value }))}
              placeholder="Enter action strategies..."
              rows={4}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 pt-4">
          <button
            type="submit"
            disabled={loading}
            className="bg-rs-green hover:bg-rs-light-green !text-rs-black font-semibold px-6 py-2 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
          <button
            type="button"
            onClick={handleClear}
            className="bg-rs-jet-light hover:bg-rs-jet-light/70 text-rs-off-white font-semibold px-6 py-2 rounded transition-colors"
          >
            Clear
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="bg-rs-jet hover:bg-rs-jet-light text-rs-off-white font-semibold px-6 py-2 rounded transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default DriversForm;
